import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  NextPage,
} from "next";
import React, { useMemo } from "react";
import cn from "classnames";
import { AppSettings } from "../../../lib/get-app-settings";
import { CustomerPortalContextProvider } from "../../../components/customer-portal/customer-portal-context";
import ConversationsTabs from "../../../components/customer-portal/conversations-list/conversations-tabs";
import loadCustomer from "../../../lib/load-customer";
import Customer, { TCustomer } from "../../../models/customer";
import Conversation, { TConversation } from "../../../models/conversation";
import loadConversations, {
  TLoadConversationsPayload,
} from "../../../lib/load-conversations";
import createVisitor from "../../../lib/create-visitor";
import cookieString from "../../../lib/cookie-string";
import type { THelpCenterStandardLayout } from "../../../lib/load-company-settings";
import StandardLayout from "../../../components/layout.standard";
import StandardLayoutHeader from "../../../components/customer-portal/layout-header.standard";
import styles from "../../../styles/customer-portal.module.css";

const TicketsStandardPage: NextPage<TTicketsStandardPageProps> = ({
  appSettings,
  customerData,
  customerConversationsData,
  customerConversationsTotal,
  accountConversationsData,
  accountConversationsTotal,
}) => {
  const customer = useMemo(
    () => customerData && new Customer(customerData),
    [customerData]
  );

  const initial = useMemo(
    () => ({
      customerConversations: customerConversationsData.map(
        (c) => new Conversation(c)
      ),
      customerTotal: customerConversationsTotal,
      accountConversations: accountConversationsData.map(
        (c) => new Conversation(c)
      ),
      accountTotal: accountConversationsTotal,
    }),
    [
      accountConversationsData,
      accountConversationsTotal,
      customerConversationsData,
      customerConversationsTotal,
    ]
  );

  return (
    <CustomerPortalContextProvider
      customer={customer}
      appSettings={appSettings}
      initial={initial}
    >
      <StandardLayout
        className={cn(
          styles.customerPortalLayout,
          styles.customerPortalLayoutStandard
        )}
        appSettings={appSettings}
        pageTitle={appSettings.supportPortalTitle}
        pageDescription={appSettings.supportPortalDescription}
        header={<StandardLayoutHeader appSettings={appSettings} />}
        hideSearch
        hideFooter
        noContentTopPadding
      >
        <ConversationsTabs appSettings={appSettings} />
      </StandardLayout>
    </CustomerPortalContextProvider>
  );
};

export default TicketsStandardPage;

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
  appSettings: AppSettings & { layout: THelpCenterStandardLayout }
): Promise<ReturnType<GetServerSideProps<TTicketsStandardPageProps>>> => {
  const { appId } = appSettings;

  const cookieIdKey = `atlasIdentity${appId}`;
  const currentCustomerId = context.req.cookies[cookieIdKey];

  let filters;
  let sorts;
  try {
    filters =
      context.query.filters && JSON.parse(context.query.filters as string);
    sorts = context.query.sorts && JSON.parse(context.query.sorts as string);
  } catch (error) {
    console.error(error);
  }

  const customerTabFilters: TLoadConversationsPayload = {
    sort: sorts?.customer,
    filters: filters?.customer,
  };
  const accountTabFilters: TLoadConversationsPayload = {
    sort: sorts?.account,
    filters: filters?.account,
  };

  const [
    customerData,
    { data: customerConversationsData, total: customerConversationsTotal },
    { data: accountConversationsData, total: accountConversationsTotal },
  ] = await Promise.all([
    currentCustomerId
      ? loadCustomer(appId, currentCustomerId).catch((error) => {
          console.error(error);
          return createVisitor(appId);
        })
      : createVisitor(appId),
    currentCustomerId
      ? loadConversations(appId, context, false, customerTabFilters).catch(
          handleConversationsError
        )
      : emptyConversationsResponse,
    currentCustomerId
      ? loadConversations(appId, context, true, accountTabFilters).catch(
          handleConversationsError
        )
      : emptyConversationsResponse,
  ]);

  if (!currentCustomerId && customerData) {
    context.res.setHeader(
      "Set-Cookie",
      cookieString(cookieIdKey, customerData.id, context, appSettings)
    );
  }

  return {
    props: {
      appSettings,
      customerData,
      customerConversationsData,
      customerConversationsTotal,
      accountConversationsData,
      accountConversationsTotal,
    },
  };
};

const emptyConversationsResponse: {
  data: TConversation[];
  total: 0;
} = { data: [], total: 0 };

function handleConversationsError(error: any) {
  console.error(error);
  return emptyConversationsResponse;
}

export type TTicketsStandardPageProps = {
  appSettings: AppSettings;
  customerData: TCustomer | null;
  customerConversationsData: TConversation[];
  customerConversationsTotal: number;
  accountConversationsData: TConversation[];
  accountConversationsTotal: number;
};
